import deepMerge from 'deepmerge'

import * as types from '../mutation-types'

import { loadMessage } from '@/plugins/i18n'
import axios from '@/plugins/axios'

import { VIEWS } from '@/config'

const rootElement = document.getElementById('rs-frontend-app')

const defaultAppearanceSettings = () => ({
  design: {
    accent_color: '#1976D2',
    template: 'multi-options',
    layout: null,
    variantCalendar: 'inline' // inline | dropdown
  },
  tour: {
    is_product_name_with_duration: false,
    is_option_name_with_price: false
  }
})

const defaultDisplaySettings = (appearanceSettings) => ({
  is_calendar_full_width: appearanceSettings.design.template === 'row',
  is_widget: appearanceSettings.design.template !== 'row'
})

export const state = () => {
  const { isCalendarFullWidth, isWidget, template, accentColor } = rootElement.dataset

  // Preset settings override settings from DB
  const presetDisplaySettings = {}
  if (isCalendarFullWidth)
    presetDisplaySettings.is_calendar_full_width = Boolean(isCalendarFullWidth)
  if (isWidget) presetDisplaySettings.is_widget = Boolean(isWidget)

  const presetAppearanceSettings = { design: { accent_color: accentColor } }
  if (template) presetAppearanceSettings.design.template = template

  return {
    locale: 'en',
    productId: null,
    defaultOptionId: null,
    view: VIEWS.MAIN,

    settingsLoading: true,
    settingsError: null,

    generalSettings: {},
    appearanceSettings: {},
    supplierSettings: {},
    displaySettings: {},

    preset: { presetDisplaySettings, presetAppearanceSettings }
  }
}

export const mutations = {
  [types.SET_LOCALE](state, payload) {
    state.locale = payload
  },
  [types.SET_PRODUCT_ID](state, payload) {
    state.productId = payload
  },
  [types.SET_DEFAULT_OPTION_ID](state, payload) {
    state.defaultOptionId = payload
  },
  [types.SET_VIEW](state, payload) {
    state.view = payload
  },

  [types.SET_SETTINGS_LOADING](state, payload) {
    state.settingsLoading = payload
  },
  [types.SET_SETTINGS_ERROR](state, payload) {
    state.settingsError = payload
  },

  [types.SET_GENERAL_SETTINGS](state, payload) {
    state.generalSettings = payload
  },
  [types.SET_APPEARANCE_SETTINGS](state, payload) {
    state.appearanceSettings = payload
  },
  [types.SET_SUPPLIER_SETTINGS](state, payload) {
    state.supplierSettings = payload
  },
  [types.SET_DISPLAY_SETTINGS](state, payload) {
    state.displaySettings = payload
  }
}

export const actions = {
  setProductId({ commit }, productId) {
    commit(types.SET_PRODUCT_ID, productId)
  },

  setDefaultOptionId({ commit }, defaultOptionId) {
    commit(types.SET_DEFAULT_OPTION_ID, defaultOptionId)
  },

  setView({ commit }, view) {
    commit(types.SET_VIEW, view)
  },

  async fetchSettings({ commit, state }) {
    try {
      const { data } = await axios.get('settings', {
        params: { setting_names: ['general', 'appearance', 'supplier'] }
      })

      const { presetDisplaySettings, presetAppearanceSettings } = state.preset

      const rowGeneralSettings = JSON.parse(data.general_settings)
      const rowAppearanceSettings = JSON.parse(data.appearance_settings)
      const rowSupplierSettings = JSON.parse(data.supplier_settings)

      const appearanceSettings = deepMerge.all([
        defaultAppearanceSettings(),
        rowAppearanceSettings,
        presetAppearanceSettings
      ])
      const displaySettings = deepMerge(
        defaultDisplaySettings(appearanceSettings),
        presetDisplaySettings
      )

      commit(types.SET_GENERAL_SETTINGS, rowGeneralSettings)
      commit(types.SET_SUPPLIER_SETTINGS, rowSupplierSettings)
      commit(types.SET_APPEARANCE_SETTINGS, appearanceSettings)
      commit(types.SET_DISPLAY_SETTINGS, displaySettings)
    } catch (e) {
      commit(types.SET_SETTINGS_ERROR, e)
      throw e
    } finally {
      commit(types.SET_SETTINGS_LOADING, false)
    }
  },

  setDisplaySettings({ commit }, payload) {
    commit(types.SET_DISPLAY_SETTINGS, payload)
  },

  async setLocale({ commit }, locale) {
    await loadMessage(locale)
    commit(types.SET_LOCALE, locale)
  }
}
